// Coach route — orchestrates the sessions rail (left) + thread (right).
//
// Follows design/UI : UX v1.0/for-engineering-coach-history-memory/.
// MR A gave us the backend (sessions, messages scoped by session_id).
// MR B gave us the reply surface (ReplyDocument, PromptBlock, Composer, etc.).
// MR C (this file) wires them: sessions rail, session switching, rename-in-
// header, welcome/resume states, mobile sheet.

import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CoachMessage,
  CoachProject,
  CoachSession,
  PatchCoachSessionRequest,
} from '@gc-coach/shared';
import { titleFromMessage } from '@gc-coach/shared';
import { useI18n } from '../i18n/index.js';
import {
  listCoachProjects,
  listCoachSessions,
  listSessionMessages,
  patchCoachSession,
  sendCoachFeedback,
  sendCoachMessage,
} from '../api/endpoints.js';
import { CoachComposer } from '../coach/CoachComposer.js';
import { DictationStrip } from '../coach/dictation/DictationStrip.js';
import { useDictation } from '../coach/dictation/useDictation.js';
import { env } from '../config/env.js';
import { CoachResume, CoachWelcome } from '../coach/CoachEmptyStates.js';
import { PromptBlock } from '../coach/PromptBlock.js';
import { CoachReplyMessage } from '../coach/CoachReplyMessage.js';
import { ReplySkeleton, type PipelineStage } from '../coach/ReplySkeleton.js';
import { LiveReplyBubble } from '../coach/LiveReplyBubble.js';
import { isCoachStreamEnabled, sendCoachMessageStream } from '../api/coach-stream.js';
import { ProjectHeader } from '../coach/ProjectHeader.js';
import { SessionsRail } from '../coach/SessionsRail.js';
import type { FeedbackReason, FeedbackVerdict } from '../coach/FeedbackInline.js';
import styles from '../coach/Coach.module.css';

export function CoachPage() {
  const { t } = useI18n();
  const [sessions, setSessions] = useState<CoachSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [projects, setProjects] = useState<CoachProject[]>([]);
  const [messages, setMessages] = useState<CoachMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [composer, setComposer] = useState('');
  const [sending, setSending] = useState(false);
  // Dictation hook — always called (React rules), but only passed to the
  // composer when the feature flag is on. The hook is cheap when idle
  // (no mic access, no network, no timers).
  const dictationApi = useDictation();
  const dictation = env.dictationEnabled ? dictationApi : undefined;
  // When streaming is enabled, the handler emits `stage` events as the
  // backend progresses from retrieval → drafting. ReplySkeleton reads this
  // to light the matching stage; null = pre-first-event (shows the single
  // generic line) OR no streaming at all.
  const [sendingStage, setSendingStage] = useState<PipelineStage | null>(null);
  // Growing coach-reply text during token streaming. Null while nothing is
  // streaming OR before the first `token` event. Rendered as a live bubble
  // between the ReplySkeleton and the composer; swapped for the final
  // persisted CoachMessage on the `done` event.
  const [liveReplyText, setLiveReplyText] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState<string | null>(null);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [feedbackByMessage, setFeedbackByMessage] = useState<Record<string, FeedbackVerdict>>({});
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  // true when the user explicitly asked for a fresh chat ("+ New session")
  // after already having history. Overrides the CoachResume "pick up where
  // you left off" prompt so they get a blank compose state, Claude.ai-style.
  // Reset whenever they pick a session, send a first message, or on route
  // mount (initial render falls through to CoachResume normally).
  const [startingNew, setStartingNew] = useState(false);
  const tbodyRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  const activeSession = sessions.find((s) => s.id === activeSessionId) ?? null;

  // Load the projects list on mount + whenever ProjectsSection reports a
  // change. Used for session-item name resolution + ProjectHeader rendering.
  async function reloadProjects() {
    const result = await listCoachProjects();
    if (result.ok) setProjects([...result.data.projects]);
  }
  useEffect(() => {
    void reloadProjects();
  }, []);

  // Load sessions on mount or when the active project filter changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSessionsLoading(true);
      const result = await listCoachSessions();
      if (cancelled) return;
      if (result.ok) {
        const all = result.data.sessions;
        // Client-side project filter (keeps the rail list in-memory consistent
        // with the ProjectsSection filter without re-fetching per toggle).
        setSessions(
          activeProjectId ? all.filter((s) => s.projectId === activeProjectId) : all,
        );
      }
      setSessionsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeProjectId]);

  // When active session changes, load its messages.
  const loadMessages = useCallback(async (sessionId: string | null) => {
    if (!sessionId) {
      setMessages([]);
      return;
    }
    setMessagesLoading(true);
    const result = await listSessionMessages(sessionId);
    if (result.ok) {
      setMessages(result.data.messages);
    } else {
      setMessages([]);
      setErrorBanner(t('coach.loadError'));
    }
    setMessagesLoading(false);
  }, [t]);

  useEffect(() => {
    void loadMessages(activeSessionId);
  }, [activeSessionId, loadMessages]);

  // Scroll to bottom on message changes or when the skeleton appears.
  useEffect(() => {
    const el = tbodyRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, sending]);

  // Focus the rename input when it opens.
  useEffect(() => {
    if (renameValue !== null) {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }
  }, [renameValue]);

  async function handleSelectSession(id: string) {
    // Reset the fresh-chat intent — picking a specific session supersedes
    // any pending "+ New session" state.
    setStartingNew(false);
    // Fire-and-forget: end the previous session so the auto-title job runs
    // for it. We don't await — the user wants the next session open now,
    // and the title is a background concern.
    void endSessionIfOpen(activeSessionId, id);
    setActiveSessionId(id);
    setMobileSheetOpen(false);
    setRenameValue(null);
  }

  function handleNewSession() {
    // Claude.ai-style lazy create: no server call here. We just clear the
    // thread view and leave activeSessionId=null. The backend creates the
    // session (with its title derived from the message) when the user sends
    // their first message. No message → no row → no accumulated clutter.
    void endSessionIfOpen(activeSessionId, null);
    setActiveSessionId(null);
    setMessages([]);
    setMobileSheetOpen(false);
    setRenameValue(null);
    setLiveReplyText(null);
    setSendingStage(null);
    setStartingNew(true);
  }

  /**
   * End the session the user is leaving (if any) so the backend fires the
   * auto-title + summary job. Only ends sessions with real content; empty
   * ones are skipped by the backend auto-titler anyway, but we avoid the
   * round-trip here.
   */
  async function endSessionIfOpen(previousId: string | null, nextId: string | null) {
    if (!previousId || previousId === nextId) return;
    const prev = sessions.find((s) => s.id === previousId);
    if (!prev || prev.endedAt || prev.messageCount < 2) return;
    const result = await patchCoachSession(previousId, { end: true });
    if (result.ok) {
      // The handler returns the possibly-titled session; splice it back in
      // so the rail picks up the new title/summary without a reload.
      setSessions((all) => all.map((s) => (s.id === previousId ? result.data.session : s)));
    }
  }

  /**
   * Row-level kebab actions in the SessionsRail / SearchResults flow through
   * here. Archive hides the session from the default rail (filtered via
   * include_archived=false server-side); unarchive brings it back. Project
   * moves retarget projectId. On archive, if the archived session was the
   * active one we clear the selection so the thread doesn't render a hidden
   * row's history.
   */
  async function handlePatchSession(sessionId: string, patch: PatchCoachSessionRequest) {
    const result = await patchCoachSession(sessionId, patch);
    if (!result.ok) return;
    const updated = result.data.session;
    // When archived, drop from the default list so it disappears from the
    // rail immediately. The search panel re-fetches with include_archived
    // on its own; no manual reconciliation needed there.
    if (updated.archived) {
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
      }
    } else {
      setSessions((prev) => {
        const existing = prev.find((s) => s.id === sessionId);
        if (existing) {
          return prev.map((s) => (s.id === sessionId ? updated : s));
        }
        // Unarchive from search: splice back into the list, newest-first.
        return [updated, ...prev];
      });
    }
    // A project reassignment moves the row between projects, so counts +
    // lastActive on the projects list change. Reload so ProjectsSection
    // reflects the new tallies.
    if (patch.projectId !== undefined) void reloadProjects();
  }

  async function handleStarterOrFirstMessage(text: string) {
    // No active session yet: send with no sessionId; the backend will create
    // one and return its id. We then insert it at the top of the sessions
    // list and set it active.
    await submitMessage(text, null);
  }

  async function handleComposerSubmit() {
    const text = composer.trim();
    if (!text || sending) return;
    setComposer('');
    await submitMessage(text, activeSessionId);
  }

  async function submitMessage(text: string, sessionId: string | null) {
    setSending(true);
    setSendingStage(null);
    const optimisticId = `pending-${Date.now()}`;
    const optimistic: CoachMessage = {
      id: optimisticId,
      sessionId: sessionId ?? '',
      role: 'user',
      text,
      createdAt: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);

    // Streaming vs non-streaming — same final result shape (user message +
    // coach message + session id). Streaming also flips sendingStage as
    // backend progress events arrive so the ReplySkeleton can light the
    // honest stage copy instead of the generic single line.
    const useStream = isCoachStreamEnabled();
    let userMessage: CoachMessage | null = null;
    let coachMessage: CoachMessage | null = null;
    let landedSessionId: string | null = null;

    if (useStream) {
      // Buffer growing text locally — React setState inside the callback
      // would re-render on every token; this stays off the render path
      // and we flush via setLiveReplyText as we go.
      let liveBuf = '';
      const result = await sendCoachMessageStream({
        body: {
          text,
          ...(sessionId ? { sessionId } : {}),
          // Lazy-create path: tell the backend which project the new
          // session should land in (the user clicked "+ New session"
          // while a project filter was active).
          ...(!sessionId && activeProjectId ? { projectId: activeProjectId } : {}),
        },
        onEvent: (evt) => {
          if (evt.kind === 'stage') {
            setSendingStage(evt.stage === 'retrieving' ? 'retrieval' : 'drafting');
          } else if (evt.kind === 'token') {
            liveBuf += evt.delta;
            setLiveReplyText(liveBuf);
          } else if (evt.kind === 'done') {
            userMessage = evt.userMessage;
            coachMessage = evt.coachMessage;
            landedSessionId = evt.sessionId;
          }
        },
      });
      setSending(false);
      setSendingStage(null);
      setLiveReplyText(null);
      if (!result.ok || !userMessage || !coachMessage || !landedSessionId) {
        setErrorBanner(t('coach.sendError'));
        setMessages((m) => m.filter((msg) => msg.id !== optimisticId));
        return;
      }
    } else {
      const result = await sendCoachMessage({
        text,
        ...(sessionId ? { sessionId } : {}),
        ...(!sessionId && activeProjectId ? { projectId: activeProjectId } : {}),
      });
      setSending(false);
      if (!result.ok) {
        setErrorBanner(t('coach.sendError'));
        setMessages((m) => m.filter((msg) => msg.id !== optimisticId));
        return;
      }
      userMessage = result.data.userMessage;
      coachMessage = result.data.coachMessage;
      landedSessionId = result.data.sessionId;
    }

    // Branch above returns on error; by here all three are set.
    const finalUser = userMessage!;
    const finalCoach = coachMessage!;
    const finalSessionId: string = landedSessionId!;

    setMessages((m) =>
      m
        .filter((msg) => msg.id !== optimisticId)
        .concat([finalUser, finalCoach]),
    );

    // First message landed; back to normal "has active session" state so
    // any later load-time computations don't still treat this as a fresh
    // starting intent.
    setStartingNew(false);
    // If the server created a new session (no activeSessionId before),
    // ensure it's represented in the sessions list and set active.
    if (!sessionId || activeSessionId !== finalSessionId) {
      setActiveSessionId(finalSessionId);
      setSessions((prev) => {
        if (prev.some((s) => s.id === finalSessionId)) return prev;
        // Fabricate a minimal CoachSession until the next full list reload
        // (which happens on next mount). Match the title the backend just
        // derived from the same text so the row shows the right label the
        // same tick it appears, no flash of "New session · now".
        const nowIso = new Date().toISOString();
        const fresh: CoachSession = {
          id: finalSessionId,
          title: titleFromMessage(text),
          summary: null,
          messageCount: 2,
          createdAt: nowIso,
          updatedAt: nowIso,
          lastMessageAt: nowIso,
          ...(activeProjectId ? { projectId: activeProjectId } : {}),
        };
        return [fresh, ...prev];
      });
      // New session filed into a project: refresh projects so sessionCount
      // + lastActive on the ProjectsSection reflect the new state.
      if (!sessionId && activeProjectId) void reloadProjects();
    } else {
      // Bump message_count + last_message_at locally for the active session.
      setSessions((prev) =>
        prev.map((s) =>
          s.id === finalSessionId
            ? {
                ...s,
                messageCount: s.messageCount + 2,
                lastMessageAt: finalCoach.createdAt,
                updatedAt: finalCoach.createdAt,
              }
            : s,
        ),
      );
    }
    // If the session (new or existing) belongs to a project, refresh the
    // projects list so the ProjectHeader's lastActive updates immediately.
    const landedSession = sessions.find((s) => s.id === finalSessionId);
    const landedProjectId = landedSession?.projectId ?? activeProjectId;
    if (landedProjectId) void reloadProjects();
  }

  async function handleVerdict(messageId: string, verdict: FeedbackVerdict) {
    setFeedbackByMessage((f) => ({ ...f, [messageId]: verdict }));
    if (verdict === 'up') {
      await sendCoachFeedback({ messageId, verdict: 'up' });
    }
  }

  async function handleSubmitReason(
    messageId: string,
    reason: FeedbackReason | null,
    note: string,
  ) {
    const reasonText =
      reason === 'off_topic'
        ? t('coach.fb.r1')
        : reason === 'too_generic'
        ? t('coach.fb.r2')
        : reason === 'missed_context'
        ? t('coach.fb.r3')
        : '';
    const body = [reasonText, note].filter(Boolean).join(' · ').slice(0, 500);
    await sendCoachFeedback({
      messageId,
      verdict: 'down',
      ...(body ? { reason: body } : {}),
    });
  }

  async function handleRenameSubmit() {
    if (!activeSession || renameValue === null) {
      setRenameValue(null);
      return;
    }
    const next = renameValue.trim();
    if (!next || next === activeSession.title) {
      setRenameValue(null);
      return;
    }
    const result = await patchCoachSession(activeSession.id, { title: next });
    if (result.ok) {
      setSessions((prev) => prev.map((s) => (s.id === activeSession.id ? result.data.session : s)));
    }
    setRenameValue(null);
  }

  // ─── Render ─────────────────────────────────────────────────────────────

  const hasSessions = sessions.length > 0;
  // CoachWelcome fires on the first-time empty state AND whenever the user
  // explicitly chose "+ New session" — both are compose-ready moments.
  // CoachResume ("pick up where you left off") only fires on the initial
  // mount with existing history, so it never collides with a deliberate
  // new-chat intent.
  const showWelcome = !activeSession && !sessionsLoading && (!hasSessions || startingNew);
  const showResume = !activeSession && hasSessions && !sessionsLoading && !startingNew;
  const mostRecentForResume = showResume ? sessions[0] : null;

  const titleText = activeSession?.title ?? t('coach.author');

  return (
    <div className={styles.work}>
      <div className={mobileSheetOpen ? styles.railSheet : styles.railSheet}>
        <SessionsRail
          sessions={sessions}
          activeSessionId={activeSessionId}
          activeProjectId={activeProjectId}
          projects={projects}
          onSelect={handleSelectSession}
          onNewSession={handleNewSession}
          onSelectProject={setActiveProjectId}
          onProjectsChanged={() => void reloadProjects()}
          onPatchSession={(id, patch) => void handlePatchSession(id, patch)}
          loading={sessionsLoading}
        />
        {mobileSheetOpen && (
          <button
            type="button"
            className={styles.railSheetClose}
            onClick={() => setMobileSheetOpen(false)}
            aria-label={t('history.mobileClose')}
          >
            ✕
          </button>
        )}
      </div>

      <section className={styles.thread}>
        {activeProjectId && (() => {
          const activeProject = projects.find((p) => p.id === activeProjectId);
          if (!activeProject) return null;
          return (
            <ProjectHeader
              project={activeProject}
              onProjectUpdated={(next) =>
                setProjects((prev) => prev.map((p) => (p.id === next.id ? next : p)))
              }
              onClear={() => setActiveProjectId(null)}
            />
          );
        })()}
        <header className={styles.thh}>
          <button
            type="button"
            className={styles.mobileSheetToggle}
            onClick={() => setMobileSheetOpen(true)}
          >
            ≡ {t('history.mobileToggle')}
          </button>
          {activeSession && renameValue !== null ? (
            <input
              ref={renameInputRef}
              className={styles.thhTitleInput}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={() => void handleRenameSubmit()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleRenameSubmit();
                } else if (e.key === 'Escape') {
                  setRenameValue(null);
                }
              }}
            />
          ) : (
            <h1
              className={styles.thhTitle}
              title={activeSession ? t('history.rename.hint') : undefined}
              onClick={() => {
                if (activeSession) setRenameValue(activeSession.title ?? '');
              }}
            >
              {titleText}
            </h1>
          )}
          <span className={styles.thhLegal}>{t('coach.legal')}</span>
        </header>

        <div ref={tbodyRef} className={styles.tbody}>
          <div className={styles.col}>
            {errorBanner && <div className={styles.err}>{errorBanner}</div>}

            {showWelcome && <CoachWelcome onStarter={(text) => void handleStarterOrFirstMessage(text)} />}

            {showResume && mostRecentForResume && (
              <CoachResume
                title={
                  mostRecentForResume.title ??
                  t('history.untitled').replace(
                    '{date}',
                    new Date(mostRecentForResume.lastMessageAt ?? mostRecentForResume.createdAt).toLocaleDateString(),
                  )
                }
                summary={mostRecentForResume.summary}
                onResume={() => void handleSelectSession(mostRecentForResume.id)}
                onStartNew={() => void handleNewSession()}
              />
            )}

            {activeSession &&
              !messagesLoading &&
              messages.map((m) => {
                if (m.role === 'user') {
                  return <PromptBlock key={m.id} text={m.text} />;
                }
                return (
                  <CoachReplyMessage
                    key={m.id}
                    messageId={m.id}
                    text={m.text}
                    citations={m.citations ?? []}
                    verdict={feedbackByMessage[m.id] ?? null}
                    onVerdict={(v) => void handleVerdict(m.id, v)}
                    onSubmitReason={(reason, note) => handleSubmitReason(m.id, reason, note)}
                  />
                );
              })}

            {sending && liveReplyText === null && (
              <ReplySkeleton {...(sendingStage ? { activeStage: sendingStage } : {})} />
            )}
            {sending && liveReplyText !== null && (
              <LiveReplyBubble text={liveReplyText} />
            )}
          </div>
        </div>

        {!showWelcome && (
          <CoachComposer
            value={composer}
            onChange={setComposer}
            onSubmit={handleComposerSubmit}
            disabled={sending || messagesLoading}
            {...(dictation ? { dictation } : {})}
          />
        )}
        {showWelcome && (
          <CoachComposer
            value={composer}
            onChange={setComposer}
            onSubmit={() => {
              const text = composer.trim();
              if (!text) return;
              setComposer('');
              void handleStarterOrFirstMessage(text);
            }}
            disabled={sending}
            {...(dictation ? { dictation } : {})}
          />
        )}
        {dictation && <DictationStrip d={dictation} />}

        <div className="sr-only" aria-live="polite">
          {sending ? t('coach.srSending') : ''}
        </div>
      </section>
    </div>
  );
}
